import React, { useState, useCallback } from 'react';
import { Container, Form, Button, Alert } from 'react-bootstrap';
import { uploadFileToS3 } from '../services/s3Service';
import { submitImageMetadata, getPresignedUrl } from '../services/imageService';

function Upload() {
    const [files, setFiles] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState('');
    
    // Status tracking for each file
    const [fileStatuses, setFileStatuses] = useState({});  // { filename: { status: 'not_started' | 'uploading' | 'done' | 'error', error?: string } }

    // Check if there are any files that need uploading
    const hasFilesToUpload = files.some(file => 
        !fileStatuses[file.name] || 
        (fileStatuses[file.name].status !== 'done' && fileStatuses[file.name].status !== 'uploading')
    );

    function handleLogout() {
        localStorage.removeItem('authToken');
        window.location.reload();
    }

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        
        const droppedFiles = Array.from(e.dataTransfer.files).filter(
            file => file.name.toLowerCase().endsWith('.tif')
        );
        
        if (droppedFiles.length === 0) {
            setError('Please only drop .tif files');
            return;
        }

        setFiles(prevFiles => [...prevFiles, ...droppedFiles]);
        
        // Initialize status for new files
        setFileStatuses(prev => {
            const newStatuses = { ...prev };
            droppedFiles.forEach(file => {
                newStatuses[file.name] = { status: 'not_started' };
            });
            return newStatuses;
        });
        
        setError('');
    }, []);

    const handleFileSelect = (event) => {
        const selectedFiles = Array.from(event.target.files).filter(
            file => file.name.toLowerCase().endsWith('.tif')
        );
        
        if (selectedFiles.length === 0) {
            setError('Please select only .tif files');
            return;
        }

        setFiles(prevFiles => [...prevFiles, ...selectedFiles]);
        
        // Initialize status for new files
        setFileStatuses(prev => {
            const newStatuses = { ...prev };
            selectedFiles.forEach(file => {
                newStatuses[file.name] = { status: 'not_started' };
            });
            return newStatuses;
        });
        
        setError('');
    };

    function generateGuid() {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
            const r = Math.random() * 16 | 0;
            // eslint-disable-next-line
            const v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    }

    const updateFileStatus = (fileName, status, error = null) => {
        setFileStatuses(prev => ({
            ...prev,
            [fileName]: { status, ...(error && { error }) }
        }));
    };

    async function uploadFile(file) {
        try {
            updateFileStatus(file.name, 'uploading');
            
            // Generate filename
            const guid = generateGuid();
            const extension = file.name.split('.').pop();
            const newFileName = `${guid}.${extension}`;
            const renamedFile = new File([file], newFileName, { type: file.type });

            const presignedUrl = await getPresignedUrl(newFileName);
            console.log(presignedUrl);

            // Submit metadata first
            await submitImageMetadata(file.name, newFileName, {});
            
            // Then upload to S3
            console.log("uploading!");
            await uploadFileToS3(presignedUrl, renamedFile);
            
            updateFileStatus(file.name, 'done');
        } catch (error) {
            console.error(`Upload failed for ${file.name}:`, error);
            updateFileStatus(file.name, 'error', 'Upload failed');
            throw error;
        }
    }

    async function handleUpload() {
        if (files.length === 0) return;
        
        setIsUploading(true);
        setError('');

        // Process files sequentially
        for (const file of files) {
            if (fileStatuses[file.name].status !== 'done') {
                try {
                    await uploadFile(file);
                } catch (error) {
                    // Continue with next file even if current one fails
                    continue;
                }
            }
        }

        setIsUploading(false);
    }

    const getStatusColor = (status) => {
        switch (status) {
            case 'not_started': return 'secondary';
            case 'uploading': return 'primary';
            case 'done': return 'success';
            case 'error': return 'danger';
            default: return 'secondary';
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case 'not_started': return 'Not Uploaded';
            case 'uploading': return 'Uploading...';
            case 'done': return 'Done';
            case 'error': return 'Failed';
            default: return 'Not Uploaded';
        }
    };

    return (
        <>
            <Button 
                variant="secondary" 
                onClick={handleLogout} 
                style={{ position: 'fixed', top: '10px', right: '10px', zIndex: 1000 }}
            >
                Log Out
            </Button>
            <Container className="mt-5">
                <div className="w-100" style={{ maxWidth: '600px', margin: '0 auto' }}>
                    <h2 className="text-center mb-4">Tif Image Upload</h2>
                    {error && <Alert variant="danger">{error}</Alert>}
                    
                    <div 
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                        style={{
                            border: '2px dashed #ccc',
                            borderRadius: '4px',
                            padding: '20px',
                            textAlign: 'center',
                            marginBottom: '20px'
                        }}
                    >
                        <p>Drag and drop .tif files here or</p>
                        <Form.Control
                            type="file"
                            onChange={handleFileSelect}
                            disabled={isUploading}
                            multiple
                            accept=".tif"
                            style={{ display: 'none' }}
                            id="fileInput"
                        />
                        <Button
                            onClick={() => document.getElementById('fileInput').click()}
                            variant="outline-primary"
                        >
                            Browse Files
                        </Button>
                    </div>

                    {files.length > 0 && (
                        <div className="mb-3">
                            {files.map((file, index) => (
                                <div 
                                    key={index}
                                    className={`d-flex align-items-center p-2 border-bottom`}
                                >
                                    <div className="flex-grow-1">
                                        <span className={`text-${getStatusColor(fileStatuses[file.name]?.status)}`}>
                                            {file.name}
                                        </span>
                                    </div>
                                    <div className="ms-2 d-flex align-items-center">
                                        <span className={`me-2 text-${getStatusColor(fileStatuses[file.name]?.status)}`}>
                                            {getStatusLabel(fileStatuses[file.name]?.status)}
                                        </span>
                                        {fileStatuses[file.name]?.status === 'uploading' && (
                                            <div className="spinner-border spinner-border-sm" role="status">
                                                <span className="visually-hidden">Uploading...</span>
                                            </div>
                                        )}
                                        {fileStatuses[file.name]?.status === 'done' && (
                                            <span className="text-success">✓</span>
                                        )}
                                        {fileStatuses[file.name]?.status === 'error' && (
                                            <span className="text-danger">✗</span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    <Button
                        variant="primary"
                        onClick={handleUpload}
                        disabled={isUploading || !hasFilesToUpload}
                        className="w-100 mb-3"
                    >
                        {isUploading ? 'Uploading...' : 'Upload All Files'}
                    </Button>

                    <div className="mt-2 text-center">
                        <small className="text-muted">
                            The processed results will be sent to your email when processing is complete.
                        </small>
                    </div>
                </div>
            </Container>
        </>
    );
}

export default Upload; 